/*
 * The Wristband screen's state, shared by the classic panel and the v3 screen.
 *
 * The interesting part is `usage`. A wristband on its own is a name and a colour; what makes
 * deleting one consequential is what it is *bound to*, and the frame (`112:317`) requires those
 * bindings to be counted before the confirmation appears.
 *
 * Nothing new is fetched to do that. `getTicketWristbands` already returns bindings per ticket and
 * `listTickets` already carries each ticket's sold count, so the join happens here — which is also
 * why this hook loads three things rather than one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type { AdminTicketDto, WristbandBinding, WristbandColor, WristbandDto } from '../../api';

export const WRISTBAND_COLOR_LABEL: Record<WristbandColor, string> = {
  red: 'แดง', blue: 'ฟ้า', green: 'เขียว', yellow: 'เหลือง',
  orange: 'ส้ม', purple: 'ม่วง', white: 'ขาว',
};

export interface WristbandUsage {
  /** Ticket types this band is bound to, by display name. */
  ticketNames: string[];
  /** Tickets already sold across those types — the orders a delete cannot touch. */
  sold: number;
  /** Bands to be handed out, summed across bindings and sold tickets. */
  bands: number;
}

export function usageOf(
  wristbandId: string,
  tickets: readonly AdminTicketDto[],
  bindings: Record<string, WristbandBinding[]>,
): WristbandUsage {
  const usage: WristbandUsage = { ticketNames: [], sold: 0, bands: 0 };
  for (const ticket of tickets) {
    const bound = (bindings[ticket.id] ?? []).find((b) => b.wristbandId === wristbandId);
    if (!bound) continue;
    usage.ticketNames.push(ticket.nameTh || ticket.nameEn);
    usage.sold += ticket.sold;
    usage.bands += ticket.sold * bound.qty;
  }
  return usage;
}

export interface WristbandsState {
  wristbands: WristbandDto[];
  tickets: AdminTicketDto[];
  bindings: Record<string, WristbandBinding[]>;
  usage: (id: string) => WristbandUsage;
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  draft: { name: string; color: WristbandColor };
  setDraft: (next: { name: string; color: WristbandColor }) => void;
  draftError: string;
  creating: boolean;
  create: () => Promise<void>;
  rename: (id: string, patch: { name?: string; color?: WristbandColor }) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useWristbands(token: string, eventSlug?: string): WristbandsState {
  const [wristbands, setWristbands] = useState<WristbandDto[] | null>(null);
  const [tickets, setTickets] = useState<AdminTicketDto[]>([]);
  const [bindings, setBindings] = useState<Record<string, WristbandBinding[]>>({});
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; color: WristbandColor }>({ name: '', color: 'red' });
  const [draftError, setDraftError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      // The bindings and the sold counts are what make a delete consequential, so they are loaded
      // with the list rather than on demand — by the time someone reaches for delete it is too late
      // to start counting.
      const [bands, ticketList, boundMap] = await Promise.all([
        adminApi.listWristbands(token, eventSlug),
        adminApi.listTickets(token, eventSlug).catch(() => [] as AdminTicketDto[]),
        adminApi.getTicketWristbands(token, eventSlug).catch(() => ({} as Record<string, WristbandBinding[]>)),
      ]);
      setWristbands(bands);
      setTickets(ticketList);
      setBindings(boundMap);
      setLoadError('');
    } catch {
      setLoadError('โหลดรายการ Wristband ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const usage = useMemo(() => (id: string) => usageOf(id, tickets, bindings), [tickets, bindings]);

  const act = useCallback(async (id: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  return {
    wristbands: wristbands ?? [],
    tickets, bindings, usage,
    loading: wristbands === null && !loadError,
    loadError, error, busyId,
    draft, setDraft, draftError, creating,
    create: async () => {
      const name = draft.name.trim();
      setDraftError('');
      if (!name) { setDraftError('ต้องตั้งชื่อ Wristband ก่อน'); return; }
      setCreating(true);
      try {
        await adminApi.createWristband(token, name, draft.color, eventSlug);
        setDraft({ name: '', color: 'red' });
        await load();
      } catch (e) {
        // The draft is left alone. A rejected name is usually a duplicate, and clearing the box
        // would make the person retype the thing the message is about.
        setDraftError(e instanceof ApiError ? e.message : 'สร้าง Wristband ไม่สำเร็จ');
      } finally {
        setCreating(false);
      }
    },
    rename: (id, patch) => act(id, () => adminApi.updateWristband(token, id, patch, eventSlug), 'บันทึกไม่สำเร็จ'),
    remove: (id) => act(id, () => adminApi.deleteWristband(token, id, eventSlug), 'ลบไม่สำเร็จ'),
  };
}
